import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { CardNumber, Cvv, ExpiryDate, Frames } from 'frames-react';

import { theme } from '../../../style/constants';
import { CHECKOUT_API_KEY } from '../../../constants';

import { submitCheckout } from '../../../services/checkout/actions';

import { toDate, nowTimestamp } from '../../../utils/dateUtils';

import ButtonComponent from '../../button/ButtonComponent';
import { LoadingComponent } from '../../loading/LoadingComponent';

import Lock from './assets/lock.svg';
import Card from './assets/card.svg';
import Calendar from './assets/calendar.svg';
import { Wrapper, Input } from './CheckoutStyle';

const CheckoutIframe = ({ i18n, submitCheckout, userInfo, order, partnerId, onReady }) => {
    const defaultCardHolder =
        userInfo.firstname && userInfo.lastname
            ? `${userInfo.lastname} ${userInfo.firstname}`
            : undefined;

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const [cardholder, setCardholder] = useState(defaultCardHolder);
    const [isValid, setValid] = useState(false);

    let cleanedPhoneNumber = userInfo.phoneNumber;
    if (cleanedPhoneNumber.startsWith('+')) {
        cleanedPhoneNumber = cleanedPhoneNumber.substring(3);
    }

    const config = {
        publicKey: CHECKOUT_API_KEY,
        schemeChoice: true,
        acceptedPaymentMethods: ['Visa', 'Maestro', 'Mastercard'],
        cardholder: {
            name: cardholder,
            billingAddress: {
                addressLine1: userInfo.billingAdress,
                addressLine2: null,
                zip: userInfo.billingPostalCode,
                city: userInfo.billingCity,
                state: null,
                country: 'FR'
            },
            phone: cleanedPhoneNumber
        },
        localization: {
            cardNumberPlaceholder: '**** **** **** ****',
            expiryMonthPlaceholder: 'MM',
            expiryYearPlaceholder: 'AA',
            cvvPlaceholder: '***'
        },
        style: {
            base: {
                fontSize: theme.buttonFontSize,
                height: '52px',
                border: `1px solid ${theme.inputBorderColor}`,
                padding: '10px',
                color: '#353535'
            },
            invalid: {
                color: error,
                border: `1px solid ${theme.error}`
            },
            placeholder: {
                base: {
                    color: '#C6C6C6'
                }
            }
        }
    };

    const errorMessages = {
        'card-number': 'Numéro de carte invalide',
        'expiry-date': `Date d'expiration minimum : ${toDate(nowTimestamp(), 'MM/YY')}`,
        cvv: 'Cryptogramme : 3 chiffres minimum'
    };

    const onFrameValidationChanged = ({ element, isValid }) => {
        setError(!isValid ? errorMessages[element] : null);
    };

    const checkReady = ({ isValid }) => {
        setValid(isValid);
    };

    const submit = () => {
        if (!cardholder) {
            setError(i18n.CARDHOLDER_ERROR);
            return;
        }
        setLoading(true);
        Frames.submitCard()
            .then(async ({ token }) => {
                let deviceSessionId;
                if (window.Risk) {
                    const risk = await window.Risk.init(CHECKOUT_API_KEY);
                    deviceSessionId = await risk.publishRiskData();
                }
                const checkoutDatas = { token, deviceSessionId };
                submitCheckout(checkoutDatas, userInfo, partnerId, order.id);
            })
            .catch(error => {
                console.log('checkout error', error);
                setLoading(false);
            });
    };

    const onCardBinChanged = ({ scheme }) => {
        setError(
            scheme === 'American Express'
                ? "Nous ne prenons pas en charge les cartes American Express. Nous vous recommandons d'utiliser une autre carte de crédit ou de débit pour effectuer votre achat."
                : null
        );
    };

    useEffect(() => {
        onReady && onReady(isValid && cardholder && cardholder !== '');
    }, [isValid, cardholder, onReady]);

    useEffect(() => {
        Frames.addEventHandler('cardValidationChanged', ({ isValid }) => setDisabled(!isValid));
        return () => Frames.removeAllEventHandlers('cardValidationChanged');
    }, []);

    return (
        <Wrapper>
            <div className="checkout-title">CARTE BANCAIRE</div>
            {loading && <LoadingComponent />}
            <div style={{ display: loading ? 'none' : 'grid' }}>
                <Frames
                    cardValidationChanged={checkReady}
                    cardBinChanged={onCardBinChanged}
                    frameValidationChanged={onFrameValidationChanged}
                    config={config}
                    ready={() => setTimeout(() => setLoading(false), 200)}>
                    <div className="checkout-grid">
                        <div className="checkout-item-group">
                            <div className="checkout-item">
                                <b>{i18n.CARD_NUMBER_LABEL}</b>
                                <CardNumber />
                                <Card className="item-icon" />
                            </div>
                            <div className="checkout-item">
                                <b>{i18n.EXPIRY_DATE_LABEL}</b>
                                <ExpiryDate />
                                <Calendar className="item-icon" />
                            </div>
                        </div>
                        <div className="checkout-item-group">
                            <div className="checkout-item">
                                <b>{i18n.CVV_LABEL}</b>
                                <Cvv />
                            </div>
                            <div className="checkout-item">
                                <b>{i18n.CARDHOLDER_LABEL}</b>
                                <Input
                                    type="text"
                                    name="name"
                                    placeholder={i18n.CARDHOLDER_PLACEHOLDER}
                                    defaultValue={cardholder}
                                    onChange={e => setCardholder(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="checkout-item-group">
                            <div className="lock-label">
                                <Lock />
                                {i18n.LOCK_LABEL}
                            </div>
                        </div>
                    </div>
                    {error && <p className="error-message">{error}</p>}
                </Frames>
                <ButtonComponent
                    disabled={disabled || !cardholder}
                    onClick={submit}
                    text={i18n.SUBMIT}
                    color="main"
                    cssClass="checkout-submit-button"
                />
            </div>
        </Wrapper>
    );
};

const mapStateToProps = state => ({
    i18n: state.i18n.IFRAMES.CHECKOUT
});

const mapDispatchToProps = {
    submitCheckout
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutIframe);



// WEBPACK FOOTER //
// ./src/components/iframes/checkout/index.jsx